/**
 * Preview the email that goes to the apartment's owner, then send it.
 *
 * The preview is not rendered here. It is fetched from the send endpoint with
 * `dry_run: true`, which builds the mail through the same code path the real
 * send uses and stops short of Resend. Rendering a lookalike in the browser
 * would make this a guess at the mail rather than the mail — and the only reason
 * to show it is so what the admin approves is what goes out.
 *
 * The recipient is editable here because neither owner record is guaranteed to
 * carry an address — `apartments.owner_user_id` may be unset and `lessors.email`
 * is nullable — and an admin has to be able to supply or correct one without
 * editing either record.
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { BundlePreview } from "@/hooks/v2/useMoveoutBundle";
import { useLanguage } from "@/contexts/LanguageContext";

export function SendBundleDialog({
  open,
  onOpenChange,
  preview,
  loading,
  sending,
  error,
  recipient,
  onRecipientChange,
  previewStale = false,
  warning,
  onSend,
  bundlePageCount,
  bundleSizeBytes,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: BundlePreview | null;
  loading: boolean;
  sending: boolean;
  error?: string | null;
  recipient: string;
  onRecipientChange: (value: string) => void;
  /**
   * The typed recipient differs from the one the preview was built for. Send stays
   * disabled until the preview catches up — approving a preview addressed to
   * someone else is not approving the mail that goes out.
   */
  previewStale?: boolean;
  /**
   * The mail builds fine but its attachment does not exist — no bundle was ever
   * assembled, or it could not be signed. Shown BEFORE the send rather than
   * reported after it: an admin approving a mail that promises documents needs
   * to know the documents are missing while they can still stop.
   */
  warning?: string | null;
  onSend: () => void;
  bundlePageCount?: number | null;
  bundleSizeBytes?: number | null;
}) {
  // Reset the local touched state whenever the dialog reopens, so a previous
  // failed attempt does not colour a fresh one.
  const { t } = useLanguage();
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) setTouched(false);
  }, [open]);

  const recipientLooksValid =
    recipient.trim().includes("@") && !recipient.trim().includes(" ");

  /**
   * Send is live only when a preview exists AND it was built for the address
   * currently typed. Anything else would let an admin approve one mail and send
   * another.
   */
  const sendBlocked =
    sending || loading || !preview || !recipientLooksValid || previewStale;
  const sizeMb =
    bundleSizeBytes != null
      ? (bundleSizeBytes / (1024 * 1024)).toFixed(1)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{
          maxWidth: 720,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          padding: 0,
        }}
      >
        <DialogHeader style={{ padding: "22px 24px 14px" }}>
          <DialogTitle style={{ fontSize: 17, fontWeight: 700 }}>
            {t("admin.sendBundle.title", "Send to the owner")}
          </DialogTitle>
          <DialogDescription style={{ fontSize: 13, color: "#64748B" }}>
            {t(
              "admin.sendBundle.description",
              "This is the email that will be sent, built by the same code that sends it. A copy goes to the coliviq inbox."
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          style={{
            padding: "0 24px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            overflowY: "auto",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
              {t("admin.sendBundle.toLabel", "To")}
            </span>
            <input
              type="email"
              value={recipient}
              onChange={(e) => {
                setTouched(true);
                onRecipientChange(e.target.value);
              }}
              placeholder="eigentuemer@example.de"
              style={{
                border: `1px solid ${
                  touched && !recipientLooksValid ? "#FCA5A5" : "#E2E7EC"
                }`,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "inherit",
              }}
            />
            {touched && !recipientLooksValid && (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#B91C1C" }}>
                {t(
                  "admin.sendBundle.invalidEmail",
                  "Enter the owner's email address."
                )}
              </span>
            )}
          </label>

          {warning && (
            <div
              style={{
                fontSize: 12.5,
                color: "#92400E",
                background: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 12,
                padding: "12px 14px",
                lineHeight: 1.5,
              }}
            >
              {warning}
            </div>
          )}

          {preview && (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  background: "#F8FAFB",
                  border: "1px solid #ECEEF1",
                  borderRadius: 12,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}
                >
                  {t("admin.sendBundle.subjectLabel", "Subject")}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {preview.subject}
                </div>
              </div>

              <div
                style={{
                  fontSize: 12.5,
                  color: "#075985",
                  background: "#F0F9FF",
                  border: "1px solid #BAE6FD",
                  borderRadius: 12,
                  padding: "12px 14px",
                  lineHeight: 1.5,
                }}
              >
                {preview.delivery === "attach" ? (
                  <>
                    {t(
                      "admin.sendBundle.attachedAs",
                      "The bundle is attached as"
                    )}{" "}
                    <strong>{preview.attachment_filenames[0]}</strong>
                    {bundlePageCount
                      ? ` — ${bundlePageCount} ${t("admin.sendBundle.pages", "pages")}`
                      : ""}
                    {sizeMb ? `, ${sizeMb} MB` : ""}.
                  </>
                ) : (
                  <>
                    {t("admin.sendBundle.tooLargePrefix", "The bundle is")}{" "}
                    <strong>
                      {t(
                        "admin.sendBundle.tooLargeBold",
                        "too large to attach"
                      )}
                    </strong>
                    {sizeMb ? ` (${sizeMb} MB)` : ""},{" "}
                    {t(
                      "admin.sendBundle.tooLargeSuffix",
                      "so the email carries a signed download link valid for 7 days."
                    )}
                  </>
                )}
                <br />
                {t("admin.sendBundle.copiedTo", "Copied to:")}{" "}
                {preview.admin_copy_to.length
                  ? preview.admin_copy_to.join(", ")
                  : t("admin.sendBundle.nobody", "nobody")}
                {preview.admin_copy_used_fallback && (
                  <>
                    {" "}
                    <em>
                      (
                      {t(
                        "admin.sendBundle.fallbackAddress",
                        "fallback address"
                      )}
                      )
                    </em>
                  </>
                )}
              </div>

              {/*
                The rendered mail, sandboxed. It is server-built HTML containing
                tenant-entered values; an iframe keeps it from inheriting or
                touching the admin app around it.
              */}
              <iframe
                title={t("admin.sendBundle.previewTitle", "Email preview")}
                sandbox=""
                srcDoc={preview.html}
                style={{
                  width: "100%",
                  height: 460,
                  border: "1px solid #ECEEF1",
                  borderRadius: 12,
                  background: "#fff",
                }}
              />
            </>
          )}

          {previewStale && !loading && (
            <div
              style={{
                fontSize: 12.5,
                color: "#92400E",
                background: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 11,
                padding: "10px 12px",
                marginTop: 12,
              }}
            >
              {t(
                "admin.sendBundle.stalePreview",
                "Rebuilding the preview for this address — Send unlocks once it matches what you will actually send."
              )}
            </div>
          )}

          {loading && !preview && (
            <div style={{ fontSize: 13, color: "#64748B", padding: "24px 0" }}>
              {t("admin.sendBundle.buildingPreview", "Building the preview…")}
            </div>
          )}

          {error && (
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: "#B91C1C",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: 12,
                padding: "12px 14px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            borderTop: "1px solid #ECEEF1",
            padding: "14px 24px",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            style={{
              border: "1px solid #E7E9EC",
              background: "#fff",
              color: "#475569",
              borderRadius: 11,
              padding: "10px 16px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: sending ? "wait" : "pointer",
            }}
          >
            {t("admin.notes.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setTouched(true);
              if (recipientLooksValid) onSend();
            }}
            disabled={sendBlocked}
            style={{
              border: "1px solid #0F5C52",
              background: sendBlocked ? "#F6F7F9" : "#0F5C52",
              color: sendBlocked ? "#94A3B8" : "#fff",
              borderRadius: 11,
              padding: "10px 18px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: sending ? "wait" : "pointer",
            }}
          >
            {sending
              ? t("admin.sendBundle.sending", "Sending…")
              : t("admin.sendBundle.sendNow", "Send now")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
